import { useSmartDetectionStore, useLoadingStore } from '../state/store'
import { AmplitudeEvents, trackAmplitudeEvent } from '../hooks/amplitude'
import { handleAndLogError, isRichApiError } from '../utils/api-errors'
import { handleErrorWithNotification } from '../utils/error-notification'

// Import from modular structure
import {
    // Types
    BATCH_SIZE,
    FORMULA_DEBOUNCE_MS,
    PendingFormulaRequest,
    SharedProgressState,
    WiseFormulaInfo,

    // Batch processing
    yieldToEventLoop,
    processBatchesConcurrent,

    // Excel operations
    readValuesInChunks,
    readTickersFromRows,
    writeDataToExcelChunked,
    writeBatchResultsToExcel,

    // Formula utilities
    parseWiseFormulaParams,
    parseArithmeticOperation,
    isRangeReference,
    isArrayLiteral,
    parseArrayLiteral,
    expandRangeToCells,
    doesStartingCellOverlapParameters,
    columnLettersToNumber,
    columnNumberToLetters,
    extractColumnFromAddress,
    calculateDataRange,
    extractRowsFromRange,

    // Registry
    addParameterCellToRegistry,
    removeFormulaFromRegistry,
    clearRegistryForWorksheet,
    getDependentWiseFormulas,
    isFormulaRegistered,
    getFormulasByColumn,
    updateFormulaLocation,
    updateFormulaDataRange,
    getFormulaInfo,

    // API
    getWiseApiData,
    getWiseApiDataBatch,
    getWisePriceApiData,
    getWisePriceApiDataBatch,
    CONCURRENT_REQUESTS,
} from './smart-detection/'

// Debounce state for batching formula requests
let pendingFormulaRequests: Map<string, PendingFormulaRequest> = new Map()
let debounceTimer: ReturnType<typeof setTimeout> | null = null
let isProcessingBatch = false

// Cached ticker scans for refresh operations (per worksheet + ticker column)
type TickerScanResult = {
    nonBlankIndices: number[]
    nonBlankValues: string[][]
    tickerRows: number[]
}

let refreshTickerCache: Map<string, TickerScanResult> = new Map()

// Function to clear all pending requests (used during cancellation)
export const clearPendingFormulaRequests = () => {
    if (debounceTimer) {
        clearTimeout(debounceTimer)
        debounceTimer = null
    }
    pendingFormulaRequests.clear()
}

// Shared progress state for unified loading UI across multiple formulas
let sharedProgress: SharedProgressState = {
    totalTickers: 0,
    completedTickers: 0,
    totalFormulas: 0,
    completedFormulas: 0,
}

export const setupSelectionTracking = async () => {
    await Excel.run(async (context) => {
        const worksheets = context.workbook.worksheets
        worksheets.load('items')
        await context.sync()

        // Add selection tracking to all existing worksheets
        worksheets.items.forEach((worksheet) => {
            worksheet.onSelectionChanged.add(async (event) => {
                // Check if selection spans multiple cells (indicates drag)
                if (event.address.includes(':')) {
                    console.log(
                        `Range selection detected on ${worksheet.name}:`,
                        event.address
                    )
                    // Handle drag completion here
                }
            })
        })

        // Also track when new worksheets are added
        context.workbook.worksheets.onAdded.add(async (event) => {
            await Excel.run(async (context) => {
                const newWorksheet = context.workbook.worksheets.getItem(
                    event.worksheetId
                )
                newWorksheet.load('name')
                await context.sync()

                newWorksheet.onSelectionChanged.add(async (event) => {
                    if (event.address.includes(':')) {
                        console.log(
                            `Range selection detected on ${newWorksheet.name}:`,
                            event.address
                        )
                        // Handle drag completion here
                    }
                })
                await context.sync()
            })
        })

        await context.sync()
    })
}

export const setupValueChangeTracking = async () => {
    await Excel.run(async (context) => {
        const worksheets = context.workbook.worksheets
        worksheets.load('items')
        await context.sync()

        // Track changes on all existing worksheets
        for (const worksheet of worksheets.items) {
            worksheet.onChanged.add(async (event) => {
                // Check if smart detection is enabled from the store
                const { useSmartDetection } = useSmartDetectionStore.getState()
                if (!useSmartDetection) return

                await Excel.run(async (context) => {
                    const ws = context.workbook.worksheets.getItem(
                        event.worksheetId
                    )
                    await handleonChangedEvent(context, ws, event)
                })
            })
        }

        // Track changes on new worksheets
        context.workbook.worksheets.onAdded.add(async (event) => {
            // Check if smart detection is enabled from the store
            const { useSmartDetection } = useSmartDetectionStore.getState()
            if (!useSmartDetection) return

            await Excel.run(async (context) => {
                const newWorksheet = context.workbook.worksheets.getItem(
                    event.worksheetId
                )
                newWorksheet.load('name')
                await context.sync()

                newWorksheet.onChanged.add(async (event) => {
                    // Check if smart detection is enabled from the store
                    const { useSmartDetection } =
                        useSmartDetectionStore.getState()
                    if (!useSmartDetection) return

                    await Excel.run(async (context) => {
                        const ws = context.workbook.worksheets.getItem(
                            event.worksheetId
                        )
                        await handleonChangedEvent(context, ws, event)
                    })
                })

                await context.sync()
            })
        })

        await context.sync()
    })
}

const processWisePriceFormula = async (
    context: Excel.RequestContext,
    worksheet: Excel.Worksheet,
    formula: string,
    addressToUse: string,
    formulaIndex: number,
    worksheetId: string,
    skipLoadingState: boolean = false,
    onSharedProgress?: (tickersCompleted: number) => void,
    sharedSignal?: AbortSignal,
    isRefresh: boolean = false // If true, scan entire ticker column for refresh
) => {
    const baseAddress = addressToUse.split(':')[0]
    const baseMatch = baseAddress.match(/([A-Z]+)(\d+)/)
    if (!baseMatch) return

    const baseColumn = baseMatch[1]
    const baseRow = parseInt(baseMatch[2])

    const baseColumnCode = columnLettersToNumber(baseColumn)
    const currentColumnCode = baseColumnCode + formulaIndex
    const currentColumn = columnNumberToLetters(currentColumnCode)
    const currentCellAddress = `${currentColumn}${baseRow}`

    const params = parseWiseFormulaParams(formula, 'WISEPRICE', 2)
    if (!params || params.length < 2) return

    const firstParam = params[0]
    const secondParam = params[1]
    const thirdParam = params.length > 2 ? params[2] : null
    const fourthParam = params.length > 3 ? params[3] : null
    const fifthParam = params.length > 4 ? params[4] : null

    const isRange = (param: string) => {
        const cleanParam = param.replace(/^["']|["']$/g, '')
        return cleanParam.includes(':')
    }

    // Only reject ranges for firstParam and other params (not secondParam which can be a range)
    if (
        isRange(firstParam) ||
        (thirdParam && isRange(thirdParam)) ||
        (fourthParam && isRange(fourthParam)) ||
        (fifthParam && isRange(fifthParam))
    ) {
        return
    }

    removeFormulaFromRegistry(worksheetId, currentCellAddress)

    const wiseFormulaInfo: WiseFormulaInfo = {
        worksheetId,
        formulaAddress: currentCellAddress,
        firstParam,
        secondParam,
        thirdParam,
        fourthParam,
        fifthParam,
    }

    const cellRefRegex = /^(\$?[A-Z]+)(\$?\d+)$/

    // Handle secondParam - can be single cell or range
    if (isRangeReference(secondParam)) {
        const cells = expandRangeToCells(secondParam)
        cells.forEach((cell) => {
            addParameterCellToRegistry(worksheetId, cell, wiseFormulaInfo)
        })
    } else if (cellRefRegex.test(secondParam)) {
        addParameterCellToRegistry(worksheetId, secondParam, wiseFormulaInfo)
    }

    if (thirdParam && cellRefRegex.test(thirdParam)) {
        addParameterCellToRegistry(worksheetId, thirdParam, wiseFormulaInfo)
    }

    if (fourthParam && cellRefRegex.test(fourthParam)) {
        addParameterCellToRegistry(worksheetId, fourthParam, wiseFormulaInfo)
    }

    if (fifthParam && cellRefRegex.test(fifthParam)) {
        addParameterCellToRegistry(worksheetId, fifthParam, wiseFormulaInfo)
    }

    let secondParamValues: any[]
    if (isArrayLiteral(secondParam)) {
        secondParamValues = parseArrayLiteral(secondParam)
    } else if (isRangeReference(secondParam)) {
        const paramRange = worksheet.getRange(secondParam)
        paramRange.load('values')
        await context.sync()
        secondParamValues = paramRange.values
            .flat()
            .filter((v) => v !== null && v !== undefined && v !== '')
    } else if (cellRefRegex.test(secondParam)) {
        const secondParamRange = worksheet.getRange(secondParam)
        secondParamRange.load('values')
        await context.sync()
        secondParamValues = [secondParamRange.values[0][0]]
    } else {
        secondParamValues = [secondParam.replace(/^["']|["']$/g, '')]
    }

    let thirdParamValue: any = null
    if (thirdParam) {
        if (cellRefRegex.test(thirdParam)) {
            const thirdParamRange = worksheet.getRange(thirdParam)
            thirdParamRange.load('values')
            await context.sync()
            thirdParamValue = thirdParamRange.values[0][0]
        } else {
            thirdParamValue = thirdParam.replace(/^["']|["']$/g, '')
        }
    }

    let fourthParamValue: any = null
    if (fourthParam) {
        if (cellRefRegex.test(fourthParam)) {
            const fourthParamRange = worksheet.getRange(fourthParam)
            fourthParamRange.load('values')
            await context.sync()
            fourthParamValue = fourthParamRange.values[0][0]
        } else {
            fourthParamValue = fourthParam.replace(/^["']|["']$/g, '')
        }
    }

    let fifthParamValue: any = null
    if (fifthParam) {
        if (cellRefRegex.test(fifthParam)) {
            const fifthParamRange = worksheet.getRange(fifthParam)
            fifthParamRange.load('values')
            await context.sync()
            fifthParamValue = fifthParamRange.values[0][0]
        } else {
            fifthParamValue = fifthParam.replace(/^["']|["']$/g, '')
        }
    }

    const colMatch = firstParam.match(/(\$?[A-Z]+)(\$?\d+)/)
    if (colMatch) {
        const column = colMatch[1].replace('$', '')
        const row = parseInt(colMatch[2].replace('$', ''))
        const startingCell = `${column}${row + 1}`

        if (startingCell === currentCellAddress) {
            return
        }

        if (
            doesStartingCellOverlapParameters(
                startingCell,
                [secondParam, thirdParam, fourthParam, fifthParam],
                cellRefRegex
            )
        ) {
            return
        }

        // Check if the cell below the current formula (in the formula's column) contains a formula
        // This prevents columns filled with formulas from qualifying for smart detection
        const formulaColMatch = currentCellAddress.match(/([A-Z]+)(\d+)/)
        if (formulaColMatch) {
            const formulaCol = formulaColMatch[1]
            const formulaRow = parseInt(formulaColMatch[2])
            const cellBelowFormula = `${formulaCol}${formulaRow + 1}`

            const cellBelowRange = worksheet.getRange(cellBelowFormula)
            cellBelowRange.load('formulas')
            await context.sync()

            const cellBelowFormula_formula = cellBelowRange.formulas[0][0]

            if (
                typeof cellBelowFormula_formula === 'string' &&
                cellBelowFormula_formula.startsWith('=')
            ) {
                // The cell below this formula is another formula - not a smart detection scenario
                return
            }
        }

        // Set up loading state early for progress reporting during read
        const { setLoading, setAbortController } = useLoadingStore.getState()
        let abortController: AbortController | null = null

        // Show initial loading state (only if not using shared loading)
        if (!skipLoadingState) {
            setLoading({
                isLoading: true,
                message: isRefresh
                    ? 'Scanning all tickers...'
                    : 'Reading tickers...',
            })
        }

        // For refresh: use stored data range to only update originally detected cells
        // For initial smart detection: read from startingCell downward (existing behavior)
        let nonBlankIndices: number[]
        let nonBlankValues: string[][]
        let tickerRows: number[]

        if (isRefresh) {
            // Refresh mode: Use stored dataRange if available to only update cells that were
            // originally part of smart detection, avoiding overwriting unrelated data in the column.
            const formulaInfo = getFormulaInfo(worksheetId, currentCellAddress)
            const storedDataRange = formulaInfo?.dataRange

            if (storedDataRange) {
                // Use stored data range - only refresh the rows that were originally detected
                const rowsToRefresh = extractRowsFromRange(storedDataRange)
                const result = await readTickersFromRows(
                    context,
                    worksheet,
                    column, // ticker column
                    rowsToRefresh,
                    1000
                )
                nonBlankIndices = result.nonBlankIndices
                nonBlankValues = result.nonBlankValues
                tickerRows = result.tickerRows
            } else {
                // No stored data range - use same logic as initial detection
                // Read from startingCell downward (stops at first blank, avoiding unrelated data below)
                const result = await readValuesInChunks(
                    context,
                    worksheet,
                    startingCell,
                    1000,
                    skipLoadingState
                        ? undefined
                        : (message) => setLoading({ isLoading: true, message })
                )
                nonBlankIndices = result.nonBlankIndices
                nonBlankValues = result.nonBlankValues
                tickerRows = result.tickerRows
            }
        } else {
            // Initial smart detection: read from startingCell downward (existing behavior)
            const result = await readValuesInChunks(
                context,
                worksheet,
                startingCell,
                1000,
                skipLoadingState
                    ? undefined
                    : (message) => setLoading({ isLoading: true, message })
            )
            nonBlankIndices = result.nonBlankIndices
            nonBlankValues = result.nonBlankValues
            tickerRows = result.tickerRows
        }

        if (nonBlankValues.length === 0) {
            if (!skipLoadingState) {
                setLoading({ isLoading: false, message: '' })
            }
            return
        }

        // Smart detection actually ran - track the event
        trackAmplitudeEvent(AmplitudeEvents.SMART_DETECTION_RUN)

        // Update shared progress with total tickers for this formula
        if (onSharedProgress) {
            sharedProgress.totalTickers += nonBlankValues.length
        }

        // Treat "large" datasets as those with at least 2 full API batches.
        // This keeps moderate sheets (like ~1–2k tickers) on the simpler path.
        const isLargeDataset = nonBlankValues.length > BATCH_SIZE * 2

        // Prepare write parameters
        const wiseInputAddress = currentCellAddress
        const cleanAddress = wiseInputAddress.includes('!')
            ? wiseInputAddress.split('!')[1]
            : wiseInputAddress
        const wiseInputMatch = cleanAddress.match(/([A-Z]+)(\d+)/)

        if (!wiseInputMatch) return

        const wiseColumn = wiseInputMatch[1]
        const applyOperation = parseArithmeticOperation(formula, 'WISEPRICE')
        const loadingValues = secondParamValues.map(() => 'Loading...')

        // Helper to write Loading... to a batch of cells
        // Uses actual row numbers from tickerRows array (supports non-contiguous rows)
        const writeLoadingToBatch = async (
            startIndex: number,
            endIndex: number
        ) => {
            const batchIndices = nonBlankIndices.slice(startIndex, endIndex)
            if (batchIndices.length === 0) return

            const application = context.workbook.application
            application.suspendApiCalculationUntilNextSync()

            // Write Loading... one row at a time so we handle non-contiguous rows safely
            for (const idx of batchIndices) {
                if (idx < 0 || idx >= tickerRows.length) continue

                const rowNumber = tickerRows[idx]
                const startCol = wiseColumn
                const endColCode =
                    columnLettersToNumber(wiseColumn) +
                    secondParamValues.length -
                    1
                const endCol = columnNumberToLetters(endColCode)
                const rangeAddress = `${startCol}${rowNumber}:${endCol}${rowNumber}`

                const rowRange = worksheet.getRange(rangeAddress)
                // Single row => values must be 2D array with one inner row
                rowRange.values = [loadingValues]
            }
            await context.sync()
        }

        if (isLargeDataset) {
            // Only set up abort controller if not using shared signal
            if (!skipLoadingState) {
                abortController = new AbortController()
                setAbortController(abortController)
                setLoading({
                    isLoading: true,
                    message: 'Fetching and writing price data...',
                    progress: {
                        current: 0,
                        total: Math.ceil(nonBlankValues.length / BATCH_SIZE),
                        processed: 0,
                    },
                })
            }

            const signalToUse = sharedSignal || abortController?.signal

            try {
                // For large datasets, write Loading per batch and results progressively
                await processBatchesConcurrent(
                    nonBlankValues,
                    BATCH_SIZE,
                    async (batch) =>
                        getWisePriceApiDataBatch(
                            batch,
                            secondParamValues,
                            thirdParamValue,
                            fourthParamValue,
                            fifthParamValue,
                            signalToUse
                        ),
                    CONCURRENT_REQUESTS,
                    (progress) => {
                        // Report progress to shared or local loading UI
                        if (skipLoadingState) {
                            updateSharedLoadingUI(
                                progress.currentBatch,
                                progress.totalBatches,
                                progress.processedTickers
                            )
                        } else {
                            setLoading({
                                isLoading: true,
                                message: 'Fetching and writing price data...',
                                progress: {
                                    current: progress.currentBatch,
                                    total: progress.totalBatches,
                                    processed: progress.processedTickers,
                                },
                            })
                        }
                    },
                    async (_batchIndex, startIndex, endIndex) => {
                        // Write Loading... to this batch's cells before API call
                        await writeLoadingToBatch(startIndex, endIndex)
                    },
                    async (batchResult, _batchIndex, startIndex, endIndex) => {
                        // Write this batch's results immediately
                        const batchIndices = nonBlankIndices.slice(
                            startIndex,
                            endIndex
                        )
                        await writeBatchResultsToExcel(
                            context,
                            worksheet,
                            batchResult,
                            batchIndices,
                            wiseColumn,
                            tickerRows,
                            secondParamValues.length,
                            applyOperation
                        )
                        // Report completed tickers to shared progress
                        if (onSharedProgress) {
                            onSharedProgress(batchResult.length)
                        }
                    },
                    signalToUse
                )

                // Store data range in registry after all data is written
                if (tickerRows.length > 0) {
                    const dataRange = calculateDataRange(
                        tickerRows,
                        wiseColumn,
                        secondParamValues.length
                    )
                    if (dataRange) {
                        updateFormulaDataRange(
                            worksheetId,
                            currentCellAddress,
                            dataRange
                        )
                    }
                }
            } catch (error: any) {
                if (error.message === 'Operation cancelled') {
                    console.log('WISEPRICE operation cancelled by user')
                } else {
                    throw error
                }
            } finally {
                if (!skipLoadingState) {
                    setLoading({
                        isLoading: true,
                        message: 'Finishing up (Excel is recalculating)...',
                    })
                    await yieldToEventLoop()
                    setLoading({ isLoading: false, message: '' })
                    setAbortController(null)
                }
            }
        } else {
            // For small datasets, write Loading... to all cells first, then fetch
            try {
                // Write Loading... to all cells
                await writeLoadingToBatch(0, nonBlankIndices.length)

                const data = await getWisePriceApiData(
                    nonBlankValues,
                    secondParamValues,
                    thirdParamValue,
                    fourthParamValue,
                    fifthParamValue
                )

                if (data && Array.isArray(data) && data.length > 0) {
                    await writeDataToExcelChunked(
                        context,
                        worksheet,
                        data,
                        nonBlankIndices,
                        wiseColumn,
                        tickerRows,
                        secondParamValues.length,
                        applyOperation
                    )
                    // Report completed tickers to shared progress
                    if (onSharedProgress) {
                        onSharedProgress(data.length)
                    }

                    // Store data range in registry after data is written
                    const dataRange = calculateDataRange(tickerRows, wiseColumn)
                    if (dataRange) {
                        updateFormulaDataRange(
                            worksheetId,
                            currentCellAddress,
                            dataRange
                        )
                    }
                }
            } finally {
                if (!skipLoadingState) {
                    setLoading({ isLoading: false, message: '' })
                }
            }
        }
    }
}

const processWiseFormula = async (
    context: Excel.RequestContext,
    worksheet: Excel.Worksheet,
    formula: string,
    addressToUse: string,
    formulaIndex: number,
    worksheetId: string,
    skipLoadingState: boolean = false,
    onSharedProgress?: (tickersCompleted: number) => void,
    sharedSignal?: AbortSignal,
    isRefresh: boolean = false // If true, scan entire ticker column for refresh
) => {
    const baseAddress = addressToUse.split(':')[0]
    const baseMatch = baseAddress.match(/([A-Z]+)(\d+)/)
    if (!baseMatch) return

    const baseColumn = baseMatch[1]
    const baseRow = parseInt(baseMatch[2])

    const baseColumnCode = columnLettersToNumber(baseColumn)
    const currentColumnCode = baseColumnCode + formulaIndex
    const currentColumn = columnNumberToLetters(currentColumnCode)
    const currentCellAddress = `${currentColumn}${baseRow}`

    const params = parseWiseFormulaParams(formula, 'WISE', 3)
    if (!params || params.length < 3) return

    const firstParam = params[0]
    const secondParam = params[1]
    const thirdParam = params[2]
    const fourthParam = params.length > 3 ? params[3] : null
    const fifthParam = params.length > 4 ? params[4] : null

    const isRange = (param: string) => {
        const cleanParam = param.replace(/^["']|["']$/g, '')
        return cleanParam.includes(':')
    }

    // Only reject ranges for firstParam, thirdParam and other params (not secondParam which can be a range)
    if (
        isRange(firstParam) ||
        isRange(thirdParam) ||
        (fourthParam && isRange(fourthParam)) ||
        (fifthParam && isRange(fifthParam))
    ) {
        return
    }

    removeFormulaFromRegistry(worksheetId, currentCellAddress)

    const wiseFormulaInfo: WiseFormulaInfo = {
        worksheetId,
        formulaAddress: currentCellAddress,
        firstParam,
        secondParam,
        thirdParam,
        fourthParam,
        fifthParam,
    }

    const cellRefRegex = /^(\$?[A-Z]+)(\$?\d+)$/

    // Handle secondParam - can be single cell or range
    if (isRangeReference(secondParam)) {
        const cells = expandRangeToCells(secondParam)
        cells.forEach((cell) => {
            addParameterCellToRegistry(worksheetId, cell, wiseFormulaInfo)
        })
    } else if (cellRefRegex.test(secondParam)) {
        addParameterCellToRegistry(worksheetId, secondParam, wiseFormulaInfo)
    }

    if (cellRefRegex.test(thirdParam)) {
        addParameterCellToRegistry(worksheetId, thirdParam, wiseFormulaInfo)
    }

    let secondParamValues: any[]
    if (isArrayLiteral(secondParam)) {
        secondParamValues = parseArrayLiteral(secondParam)
    } else if (isRangeReference(secondParam)) {
        const paramRange = worksheet.getRange(secondParam)
        paramRange.load('values')
        await context.sync()
        secondParamValues = paramRange.values
            .flat()
            .filter((v) => v !== null && v !== undefined && v !== '')
    } else if (cellRefRegex.test(secondParam)) {
        const secondParamRange = worksheet.getRange(secondParam)
        secondParamRange.load('values')
        await context.sync()
        secondParamValues = [secondParamRange.values[0][0]]
    } else {
        secondParamValues = [secondParam.replace(/^["']|["']$/g, '')]
    }

    let thirdParamValue: any
    if (cellRefRegex.test(thirdParam)) {
        const thirdParamRange = worksheet.getRange(thirdParam)
        thirdParamRange.load('values')
        await context.sync()
        thirdParamValue = thirdParamRange.values[0][0]
    } else {
        thirdParamValue = thirdParam.replace(/^["']|["']$/g, '')
    }

    let fourthParamValue: any = null
    if (fourthParam) {
        if (cellRefRegex.test(fourthParam)) {
            const fourthParamRange = worksheet.getRange(fourthParam)
            fourthParamRange.load('values')
            await context.sync()
            fourthParamValue = fourthParamRange.values[0][0]
        } else {
            fourthParamValue = fourthParam.replace(/^["']|["']$/g, '')
        }
    }

    let fifthParamValue: any = null
    if (fifthParam) {
        if (cellRefRegex.test(fifthParam)) {
            const fifthParamRange = worksheet.getRange(fifthParam)
            fifthParamRange.load('values')
            await context.sync()
            fifthParamValue = fifthParamRange.values[0][0]
        } else {
            fifthParamValue = fifthParam.replace(/^["']|["']$/g, '')
        }
    }

    const colMatch = firstParam.match(/(\$?[A-Z]+)(\$?\d+)/)
    if (colMatch) {
        const column = colMatch[1].replace('$', '')
        const row = parseInt(colMatch[2].replace('$', ''))
        const startingCell = `${column}${row + 1}`
        if (startingCell === currentCellAddress) {
            return
        }

        if (
            doesStartingCellOverlapParameters(
                startingCell,
                [secondParam, thirdParam, fourthParam, fifthParam],
                cellRefRegex
            )
        ) {
            return
        }

        // Check if the cell below the current formula (in the formula's column) contains a formula
        // This prevents columns filled with formulas from qualifying for smart detection
        const formulaColMatch = currentCellAddress.match(/([A-Z]+)(\d+)/)
        if (formulaColMatch) {
            const formulaCol = formulaColMatch[1]
            const formulaRow = parseInt(formulaColMatch[2])
            const cellBelowFormula = `${formulaCol}${formulaRow + 1}`

            const cellBelowRange = worksheet.getRange(cellBelowFormula)
            cellBelowRange.load('formulas')
            await context.sync()

            const cellBelowFormula_formula = cellBelowRange.formulas[0][0]

            if (
                typeof cellBelowFormula_formula === 'string' &&
                cellBelowFormula_formula.startsWith('=')
            ) {
                // The cell below this formula is another formula - not a smart detection scenario
                return
            }
        }

        // Set up loading state early for progress reporting during read
        const { setLoading, setAbortController } = useLoadingStore.getState()
        let abortController: AbortController | null = null

        // Show initial loading state (only if not using shared loading)
        if (!skipLoadingState) {
            setLoading({
                isLoading: true,
                message: isRefresh
                    ? 'Scanning all tickers...'
                    : 'Reading tickers...',
            })
        }

        // For refresh: use stored data range to only update originally detected cells
        // For initial smart detection: read from startingCell downward (existing behavior)
        let nonBlankIndices: number[]
        let nonBlankValues: string[][]
        let tickerRows: number[]

        if (isRefresh) {
            // Refresh mode: Use stored dataRange if available to only update cells that were
            // originally part of smart detection, avoiding overwriting unrelated data in the column.
            const formulaInfo = getFormulaInfo(worksheetId, currentCellAddress)
            const storedDataRange = formulaInfo?.dataRange

            if (storedDataRange) {
                // Use stored data range - only refresh the rows that were originally detected
                const rowsToRefresh = extractRowsFromRange(storedDataRange)
                const result = await readTickersFromRows(
                    context,
                    worksheet,
                    column, // ticker column
                    rowsToRefresh,
                    1000
                )
                nonBlankIndices = result.nonBlankIndices
                nonBlankValues = result.nonBlankValues
                tickerRows = result.tickerRows
            } else {
                // No stored data range - use same logic as initial detection
                // Read from startingCell downward (stops at first blank, avoiding unrelated data below)
                const result = await readValuesInChunks(
                    context,
                    worksheet,
                    startingCell,
                    1000,
                    skipLoadingState
                        ? undefined
                        : (message) => setLoading({ isLoading: true, message })
                )
                nonBlankIndices = result.nonBlankIndices
                nonBlankValues = result.nonBlankValues
                tickerRows = result.tickerRows
            }
        } else {
            // Initial smart detection: read from startingCell downward (existing behavior)
            const result = await readValuesInChunks(
                context,
                worksheet,
                startingCell,
                1000,
                skipLoadingState
                    ? undefined
                    : (message) => setLoading({ isLoading: true, message })
            )
            nonBlankIndices = result.nonBlankIndices
            nonBlankValues = result.nonBlankValues
            tickerRows = result.tickerRows
        }

        if (nonBlankValues.length === 0) {
            if (!skipLoadingState) {
                setLoading({ isLoading: false, message: '' })
            }
            return
        }

        // Smart detection actually ran - track the event
        trackAmplitudeEvent(AmplitudeEvents.SMART_DETECTION_RUN)

        // Update shared progress with total tickers for this formula
        if (onSharedProgress) {
            sharedProgress.totalTickers += nonBlankValues.length
        }

        // Treat "large" datasets as those with at least 2 full API batches.
        const isLargeDataset = nonBlankValues.length > BATCH_SIZE * 2

        // Prepare write parameters
        const wiseInputAddress = currentCellAddress
        const cleanAddress = wiseInputAddress.includes('!')
            ? wiseInputAddress.split('!')[1]
            : wiseInputAddress
        const wiseInputMatch = cleanAddress.match(/([A-Z]+)(\d+)/)

        if (!wiseInputMatch) return

        const wiseColumn = wiseInputMatch[1]
        const applyOperation = parseArithmeticOperation(formula, 'WISE')
        const loadingValues = secondParamValues.map(() => 'Loading...')

        // Helper to write Loading... to a batch of cells
        // Uses actual row numbers from tickerRows array (supports non-contiguous rows)
        const writeLoadingToBatch = async (
            startIndex: number,
            endIndex: number
        ) => {
            const batchIndices = nonBlankIndices.slice(startIndex, endIndex)
            if (batchIndices.length === 0) return

            const application = context.workbook.application
            application.suspendApiCalculationUntilNextSync()

            // Write Loading... one row at a time so we handle non-contiguous rows safely
            for (const idx of batchIndices) {
                if (idx < 0 || idx >= tickerRows.length) continue

                const rowNumber = tickerRows[idx]
                const startCol = wiseColumn
                const endColCode =
                    columnLettersToNumber(wiseColumn) +
                    secondParamValues.length -
                    1
                const endCol = columnNumberToLetters(endColCode)
                const rangeAddress = `${startCol}${rowNumber}:${endCol}${rowNumber}`

                const rowRange = worksheet.getRange(rangeAddress)
                // Single row => values must be 2D array with one inner row
                rowRange.values = [loadingValues]
            }
            await context.sync()
        }

        if (isLargeDataset) {
            // Only set up abort controller if not using shared signal
            if (!skipLoadingState) {
                abortController = new AbortController()
                setAbortController(abortController)
                setLoading({
                    isLoading: true,
                    message: 'Fetching and writing data...',
                    progress: {
                        current: 0,
                        total: Math.ceil(nonBlankValues.length / BATCH_SIZE),
                        processed: 0,
                    },
                })
            }

            const signalToUse = sharedSignal || abortController?.signal

            try {
                // For large datasets, write Loading per batch and results progressively
                await processBatchesConcurrent(
                    nonBlankValues,
                    BATCH_SIZE,
                    async (batch) =>
                        getWiseApiDataBatch(
                            batch,
                            secondParamValues,
                            thirdParamValue,
                            fourthParamValue,
                            fifthParamValue,
                            signalToUse
                        ),
                    CONCURRENT_REQUESTS,
                    (progress) => {
                        // Report progress to shared or local loading UI
                        if (skipLoadingState) {
                            updateSharedLoadingUI(
                                progress.currentBatch,
                                progress.totalBatches,
                                progress.processedTickers
                            )
                        } else {
                            setLoading({
                                isLoading: true,
                                message: 'Fetching and writing data...',
                                progress: {
                                    current: progress.currentBatch,
                                    total: progress.totalBatches,
                                    processed: progress.processedTickers,
                                },
                            })
                        }
                    },
                    async (_batchIndex, startIndex, endIndex) => {
                        // Write Loading... to this batch's cells before API call
                        await writeLoadingToBatch(startIndex, endIndex)
                    },
                    async (batchResult, _batchIndex, startIndex, endIndex) => {
                        // Write this batch's results immediately
                        const batchIndices = nonBlankIndices.slice(
                            startIndex,
                            endIndex
                        )
                        await writeBatchResultsToExcel(
                            context,
                            worksheet,
                            batchResult,
                            batchIndices,
                            wiseColumn,
                            tickerRows,
                            secondParamValues.length,
                            applyOperation
                        )
                        // Report completed tickers to shared progress
                        if (onSharedProgress) {
                            onSharedProgress(batchResult.length)
                        }
                    },
                    signalToUse
                )

                // Store data range in registry after all data is written
                if (tickerRows.length > 0) {
                    const dataRange = calculateDataRange(
                        tickerRows,
                        wiseColumn,
                        secondParamValues.length
                    )
                    if (dataRange) {
                        updateFormulaDataRange(
                            worksheetId,
                            currentCellAddress,
                            dataRange
                        )
                    }
                }
            } catch (error: any) {
                if (error.message === 'Operation cancelled') {
                    console.log('WISE operation cancelled by user')
                } else {
                    throw error
                }
            } finally {
                if (!skipLoadingState) {
                    setLoading({
                        isLoading: true,
                        message: 'Finishing up (Excel is recalculating)...',
                    })
                    await yieldToEventLoop()
                    setLoading({ isLoading: false, message: '' })
                    setAbortController(null)
                }
            }
        } else {
            // For small datasets, write Loading... to all cells first, then fetch
            try {
                // Write Loading... to all cells
                await writeLoadingToBatch(0, nonBlankIndices.length)

                const data = await getWiseApiData(
                    nonBlankValues,
                    secondParamValues,
                    thirdParamValue,
                    fourthParamValue,
                    fifthParamValue
                )

                if (data && Array.isArray(data) && data.length > 0) {
                    await writeDataToExcelChunked(
                        context,
                        worksheet,
                        data,
                        nonBlankIndices,
                        wiseColumn,
                        tickerRows,
                        secondParamValues.length,
                        applyOperation
                    )
                    // Report completed tickers to shared progress
                    if (onSharedProgress) {
                        onSharedProgress(data.length)
                    }

                    // Store data range in registry after data is written
                    const dataRange = calculateDataRange(tickerRows, wiseColumn)
                    if (dataRange) {
                        updateFormulaDataRange(
                            worksheetId,
                            currentCellAddress,
                            dataRange
                        )
                    }
                }
            } finally {
                if (!skipLoadingState) {
                    setLoading({ isLoading: false, message: '' })
                }
            }
        }
    }
}

// Update shared progress - called from onBatchComplete with batch size
const updateSharedProgress = (tickersCompleted: number) => {
    sharedProgress.completedTickers += tickersCompleted
}

// Update loading UI with current formula's progress
const updateSharedLoadingUI = (
    currentBatch: number,
    totalBatches: number,
    processedTickers: number
) => {
    const { setLoading } = useLoadingStore.getState()

    // Include any queued formulas in the total count
    const totalFormulasIncludingQueued =
        sharedProgress.totalFormulas + pendingFormulaRequests.size
    const formulaProgress =
        totalFormulasIncludingQueued > 1
            ? ` (formula ${
                  sharedProgress.completedFormulas + 1
              }/${totalFormulasIncludingQueued})`
            : ''

    setLoading({
        isLoading: true,
        message: `Updating data... Excel may freeze momentarily.${formulaProgress}`,
        progress: {
            current: currentBatch,
            total: totalBatches,
            processed: processedTickers,
        },
    })
}

// Process all pending formula requests with unified loading
// Note: Formulas are processed sequentially to avoid Excel context conflicts
const processBatchedFormulas = async (): Promise<void> => {
    if (pendingFormulaRequests.size === 0 || isProcessingBatch) {
        return Promise.resolve()
    }

    isProcessingBatch = true
    const requests = Array.from(pendingFormulaRequests.values())
    pendingFormulaRequests.clear()

    const { setLoading, setAbortController } = useLoadingStore.getState()
    const abortController = new AbortController()
    setAbortController(abortController)

    // Reset shared progress
    sharedProgress = {
        totalTickers: 0,
        completedTickers: 0,
        totalFormulas: requests.length,
        completedFormulas: 0,
    }

    // Show initial loading state using the standard message
    setLoading({
        isLoading: true,
        message: 'Updating data... Excel may freeze momentarily.',
    })

    try {
        // Process each formula sequentially to avoid Excel context conflicts
        // Each formula gets its own Excel.run context
        for (let i = 0; i < requests.length; i++) {
            const request = requests[i]

            // Check for cancellation before starting each formula
            if (abortController.signal.aborted) {
                console.log(
                    'Batched formula processing cancelled before starting formula'
                )
                break
            }

            try {
                await Excel.run(async (context) => {
                    // Check again inside Excel.run in case cancelled during context setup
                    if (abortController.signal.aborted) {
                        throw new Error('Operation cancelled')
                    }

                    const worksheet = context.workbook.worksheets.getItem(
                        request.worksheetId
                    )

                    // Calculate the original cell address
                    const baseAddress = request.addressToUse.split(':')[0]
                    const baseMatch = baseAddress.match(/([A-Z]+)(\d+)/)
                    if (baseMatch) {
                        const baseColumn = baseMatch[1]
                        const baseRow = parseInt(baseMatch[2])
                        const baseColumnCode = columnLettersToNumber(baseColumn)
                        const currentColumnCode =
                            baseColumnCode + request.formulaIndex
                        const currentColumn =
                            columnNumberToLetters(currentColumnCode)
                        const originalCellAddress = `${currentColumn}${baseRow}`

                        if (request.type === 'WISE') {
                            await processWiseFormula(
                                context,
                                worksheet,
                                request.formula,
                                request.addressToUse,
                                request.formulaIndex,
                                request.worksheetId,
                                true, // skipLoadingState - use shared loading
                                updateSharedProgress,
                                abortController.signal,
                                request.isRefresh || false // Use refresh flag for full column scan
                            )
                        } else {
                            await processWisePriceFormula(
                                context,
                                worksheet,
                                request.formula,
                                request.addressToUse,
                                request.formulaIndex,
                                request.worksheetId,
                                true, // skipLoadingState - use shared loading
                                updateSharedProgress,
                                abortController.signal,
                                request.isRefresh || false // Use refresh flag for full column scan
                            )
                        }

                        // Recalculate the original cell containing the formula
                        // This ensures the original cell refreshes after smart detection completes
                        const originalCell =
                            worksheet.getRange(originalCellAddress)
                        originalCell.calculate()
                        await context.sync()
                    }
                })

                // Check for cancellation after formula completes
                if (abortController.signal.aborted) {
                    console.log(
                        'Batched formula processing cancelled after formula completed'
                    )
                    break
                }

                sharedProgress.completedFormulas++
            } catch (error: any) {
                if (
                    error.message === 'Operation cancelled' ||
                    abortController.signal.aborted
                ) {
                    console.log('Batched formula processing cancelled')
                    break
                }
                // Handle and log error appropriately (filters RichApi errors from Datadog)
                handleAndLogError(error, {
                    operation: 'process-formula-batch',
                    formulaType: request.type,
                })
                // Continue with next formula even if one fails
            }
        }
    } catch (error: any) {
        if (error.message !== 'Operation cancelled') {
            // Show user notification for critical errors during batch processing
            handleErrorWithNotification(
                error,
                {
                    operation: 'process-batched-formulas',
                    batchSize: requests.length,
                },
                {
                    showNotification: true,
                }
            )
        }
    } finally {
        isProcessingBatch = false
        const wasCancelled = abortController.signal.aborted

        if (wasCancelled) {
            // Clear any pending requests that were queued during cancellation
            pendingFormulaRequests.clear()
            if (debounceTimer) {
                clearTimeout(debounceTimer)
                debounceTimer = null
            }
        }

        // Show completion message briefly (only if not cancelled)
        if (!wasCancelled) {
            setLoading({
                isLoading: true,
                message: 'Finishing up (Excel is recalculating)...',
            })
            await yieldToEventLoop()
        }

        // Clear loading state
        setLoading({ isLoading: false, message: '' })
        setAbortController(null)

        // Check if more requests came in while we were processing (only if not cancelled)
        if (!wasCancelled && pendingFormulaRequests.size > 0) {
            debounceTimer = setTimeout(
                processBatchedFormulas,
                FORMULA_DEBOUNCE_MS
            )
        }
    }
}

// Add a formula request to the pending queue
// Returns a promise if immediate=true, otherwise returns void
const queueFormulaRequest = (
    request: PendingFormulaRequest,
    immediate: boolean = false
): Promise<void> | void => {
    // Use a unique key based on worksheet + cell address
    const baseAddress = request.addressToUse.split(':')[0]
    const baseMatch = baseAddress.match(/([A-Z]+)(\d+)/)
    if (!baseMatch) {
        return
    }

    const baseColumn = baseMatch[1]
    const baseRow = parseInt(baseMatch[2])
    const baseColumnCode = columnLettersToNumber(baseColumn)
    const currentColumnCode = baseColumnCode + request.formulaIndex
    const currentColumn = columnNumberToLetters(currentColumnCode)
    const cellAddress = `${currentColumn}${baseRow}`

    const key = `${request.worksheetId}:${cellAddress}`
    pendingFormulaRequests.set(key, request)

    // Reset debounce timer
    if (debounceTimer) {
        clearTimeout(debounceTimer)
        debounceTimer = null
    }

    // If not currently processing, start processing (immediately or with debounce)
    if (!isProcessingBatch) {
        if (immediate) {
            // Process immediately for refresh operations and return the promise
            return processBatchedFormulas()
        } else {
            // Use debounce timer for normal user edits
            debounceTimer = setTimeout(
                processBatchedFormulas,
                FORMULA_DEBOUNCE_MS
            )
        }
    } else {
        // Already processing - update UI to show queued formulas
        const { setLoading } = useLoadingStore.getState()
        const totalFormulasIncludingQueued =
            sharedProgress.totalFormulas + pendingFormulaRequests.size
        const formulaProgress = ` (formula ${
            sharedProgress.completedFormulas + 1
        }/${totalFormulasIncludingQueued})`

        setLoading({
            isLoading: true,
            message: `Processing${formulaProgress}...`,
        })
    }
}

const cleanupFormulaRegistryEntry = async (
    context: Excel.RequestContext,
    worksheetId: string,
    cellAddress: string,
    newFormula: string
) => {
    if (
        !newFormula.startsWith('=WISE(') &&
        !newFormula.startsWith('=WISEPRICE(')
    ) {
        removeFormulaFromRegistry(worksheetId, cellAddress)
    }
}

// this is the most important function ever created
export const handleonChangedEvent = async (
    context: Excel.RequestContext,
    worksheet: Excel.Worksheet,
    event: any
) => {
    if (event.changeType === 'RangeEdited') {
        // Check if this is from a refresh operation (should process immediately)
        const isRefresh = event.source === 'RefreshButton'
        let processingPromise: Promise<void> | null = null

        let addressToUse = event.address
        if (addressToUse.includes('!')) {
            addressToUse = addressToUse.split('!')[1]
        }

        const changedRange = worksheet.getRange(addressToUse)
        changedRange.load('values')
        changedRange.load('formulas')
        worksheet.load('id')
        await context.sync()

        const worksheetId = worksheet.id
        const formulas = changedRange.formulas[0]

        for (let i = 0; i < formulas.length; i++) {
            const formula = formulas[i]
            const baseAddress = addressToUse.split(':')[0]
            const baseMatch = baseAddress.match(/([A-Z]+)(\d+)/)
            if (!baseMatch) continue

            const baseColumn = baseMatch[1]
            const baseRow = parseInt(baseMatch[2])
            const baseColumnCode = baseColumn.charCodeAt(0) - 65
            const currentColumnCode = baseColumnCode + i
            const currentColumn = String.fromCharCode(currentColumnCode + 65)
            const currentCellAddress = `${currentColumn}${baseRow}`

            if (typeof formula === 'string' && formula.startsWith('=WISE(')) {
                // Queue the request (don't process immediately, we'll process at the end)
                const promise = queueFormulaRequest(
                    {
                        formula,
                        addressToUse,
                        formulaIndex: i,
                        worksheetId,
                        type: 'WISE',
                        isRefresh: isRefresh, // Pass refresh flag
                    },
                    false // Queue without immediate processing
                )
                // Track the first processing promise if refreshing
                if (isRefresh && promise && !processingPromise) {
                    processingPromise = promise
                }
            } else if (
                typeof formula === 'string' &&
                formula.startsWith('=WISEPRICE(')
            ) {
                // Queue the request (don't process immediately, we'll process at the end)
                const promise = queueFormulaRequest(
                    {
                        formula,
                        addressToUse,
                        formulaIndex: i,
                        worksheetId,
                        type: 'WISEPRICE',
                        isRefresh: isRefresh, // Pass refresh flag
                    },
                    false // Queue without immediate processing
                )
                // Track the first processing promise if refreshing
                if (isRefresh && promise && !processingPromise) {
                    processingPromise = promise
                }
            } else {
                await cleanupFormulaRegistryEntry(
                    context,
                    worksheetId,
                    currentCellAddress,
                    formula as string
                )
            }
        }

        const baseAddress = addressToUse.split(':')[0]
        const dependentFormulas = getDependentWiseFormulas(
            worksheetId,
            baseAddress
        )

        for (const dependentFormula of dependentFormulas) {
            const dependentWorksheet =
                dependentFormula.worksheetId === worksheetId
                    ? worksheet
                    : context.workbook.worksheets.getItem(
                          dependentFormula.worksheetId
                      )

            const dependentRange = dependentWorksheet.getRange(
                dependentFormula.formulaAddress
            )
            dependentRange.load('formulas')
            if (dependentFormula.worksheetId !== worksheetId) {
                dependentWorksheet.load('id')
            }
            await context.sync()

            const dependentFormulaText = dependentRange.formulas[0][0] as string
            if (
                dependentFormulaText &&
                dependentFormulaText.startsWith('=WISE(')
            ) {
                // Queue dependent formula (don't process immediately)
                queueFormulaRequest(
                    {
                        formula: dependentFormulaText,
                        addressToUse: dependentFormula.formulaAddress,
                        formulaIndex: 0,
                        worksheetId: dependentFormula.worksheetId,
                        type: 'WISE',
                    },
                    false // Queue without immediate processing
                )
            } else if (
                dependentFormulaText &&
                dependentFormulaText.startsWith('=WISEPRICE(')
            ) {
                // Queue dependent formula (don't process immediately)
                queueFormulaRequest(
                    {
                        formula: dependentFormulaText,
                        addressToUse: dependentFormula.formulaAddress,
                        formulaIndex: 0,
                        worksheetId: dependentFormula.worksheetId,
                        type: 'WISEPRICE',
                    },
                    false // Queue without immediate processing
                )
            }
        }

        // If refreshing, don't process batch here - it will be processed once at the end
        // of processFormulasForRefresh to avoid duplicate processing
        // For normal edits, process immediately with debounce (handled in queueFormulaRequest)
    }
}

const addWorksheetCleanup = async () => {
    await Excel.run(async (context) => {
        context.workbook.worksheets.onDeleted.add(async (event) => {
            clearRegistryForWorksheet(event.worksheetId)
        })
        await context.sync()
    })
}

export const initializeSmartDetection = async () => {
    await setupSelectionTracking()
    await setupValueChangeTracking()
    await addWorksheetCleanup()
}

// Helper to check if a formula should be refreshed (only WISEPRICE or WISE with TTM)
export const shouldRefreshFormula = async (
    context: Excel.RequestContext,
    worksheet: Excel.Worksheet,
    formula: string,
    formulaType: 'WISE' | 'WISEPRICE'
): Promise<boolean> => {
    try {
        // WISEPRICE formulas should always refresh
        if (formulaType === 'WISEPRICE') {
            return true
        }

        // For WISE formulas, only refresh if they contain TTM or reference a cell with TTM
        if (formulaType === 'WISE') {
            const minParams = 3
            const params = parseWiseFormulaParams(formula, 'WISE', minParams)
            if (!params || params.length < minParams) {
                return false
            }

            const thirdParam = params[2]
            const cleanThirdParam = thirdParam
                .replace(/^["']|["']$/g, '')
                .toUpperCase()
            const cellRefRegex = /^(\$?[A-Z]+)(\$?\d+)$/

            // Check if third parameter is "TTM" (case insensitive)
            if (cleanThirdParam === 'TTM') {
                return true
            }

            // Check if third parameter is a cell reference - if so, check if that cell contains "TTM"
            if (cellRefRegex.test(thirdParam)) {
                try {
                    const cellRange = worksheet.getRange(thirdParam)
                    cellRange.load('values')
                    await context.sync()
                    const cellValue = cellRange.values[0][0]
                    if (
                        cellValue &&
                        String(cellValue).toUpperCase().trim() === 'TTM'
                    ) {
                        return true
                    }
                } catch (error) {
                    // Silently handle cell reference errors - likely transient Excel issues
                    if (!isRichApiError(error)) {
                        handleAndLogError(error, {
                            operation: 'check-ttm-cell-reference',
                        })
                    }
                }
            }
        }

        return false
    } catch (error) {
        // Only log non-RichApi errors to avoid spam
        if (!isRichApiError(error)) {
            handleAndLogError(error, {
                operation: 'should-refresh-formula',
                formulaType,
            })
        }
        return false
    }
}

// Helper to check if a formula would qualify for smart detection
export const wouldQualifyForSmartDetection = async (
    context: Excel.RequestContext,
    worksheet: Excel.Worksheet,
    formula: string,
    currentCellAddress: string,
    formulaType: 'WISE' | 'WISEPRICE'
): Promise<boolean> => {
    try {
        // WISEPRICE needs 2 params, WISE needs 3
        const minParams = formulaType === 'WISEPRICE' ? 2 : 3
        const params = parseWiseFormulaParams(formula, formulaType, minParams)
        if (!params || params.length < minParams) {
            return false
        }
        const firstParam = params[0]
        const cellRefRegex = /^(\$?[A-Z]+)(\$?\d+)$/

        // Smart detection requires the first parameter to be a single cell,
        // not a range. If it's a range (e.g. A2:A1257), treat this formula
        // as a regular WISE/WISEPRICE call and skip smart detection.
        if (isRangeReference(firstParam)) {
            return false
        }

        // Check if firstParam is a cell reference (required for smart detection)
        const colMatch = firstParam.match(/(\$?[A-Z]+)(\$?\d+)/)
        if (!colMatch) {
            return false
        }

        const column = colMatch[1].replace('$', '')
        const row = parseInt(colMatch[2].replace('$', ''))
        const startingCell = `${column}${row + 1}`

        // If starting cell is the same as current cell, no smart detection
        if (startingCell === currentCellAddress) {
            return false
        }

        // For WISE functions, we allow fixed years for smart detection.
        // Fixed years (like "2020") can still benefit from smart detection when there are
        // multiple tickers in a column. The original logic was too restrictive and prevented
        // valid smart detection scenarios. Smart detection works with:
        // - Fixed years (e.g., "2020", "2019")
        // - Dynamic periods (e.g., "TTM", "LY")
        // - Cell references
        // Note: The actual smart detection processing will handle all these cases correctly.

        // Check if starting cell overlaps with any parameters (similar to actual smart detection logic)
        const otherParams =
            formulaType === 'WISEPRICE'
                ? [params[1], params[2], params[3], params[4]].filter(Boolean)
                : [params[1], params[2], params[3], params[4]].filter(Boolean)

        if (
            doesStartingCellOverlapParameters(
                startingCell,
                otherParams,
                cellRefRegex
            )
        ) {
            return false
        }

        // Check if the cell below the current formula (in the formula's column) contains a formula
        // This prevents columns filled with formulas from qualifying for smart detection
        const formulaColMatch = currentCellAddress.match(/([A-Z]+)(\d+)/)
        if (formulaColMatch) {
            const formulaCol = formulaColMatch[1]
            const formulaRow = parseInt(formulaColMatch[2])
            const cellBelowFormula = `${formulaCol}${formulaRow + 1}`

            const cellBelowRange = worksheet.getRange(cellBelowFormula)
            cellBelowRange.load('formulas')
            await context.sync()

            const cellBelowFormula_formula = cellBelowRange.formulas[0][0]

            if (
                typeof cellBelowFormula_formula === 'string' &&
                cellBelowFormula_formula.startsWith('=')
            ) {
                // The cell below this formula is another formula - not a smart detection scenario
                return false
            }
        }

        // Check if there are any tickers below (quick check - just read first few cells)
        // Use the same chunked reading approach as the actual smart detection
        const { nonBlankIndices, nonBlankValues, tickerRows } =
            await readValuesInChunks(
                context,
                worksheet,
                startingCell,
                10, // Just check first 10 cells for qualification
                undefined // No progress callback needed for quick check
            )

        const hasTickers = nonBlankValues.length > 0

        if (!hasTickers) {
            console.log(
                `[QUALIFY] Formula at ${currentCellAddress} does not qualify: no tickers found below startingCell ${startingCell}`
            )
        }

        return hasTickers
    } catch (error) {
        // Only log non-RichApi errors to avoid spam
        if (!isRichApiError(error)) {
            handleAndLogError(error, {
                operation: 'would-qualify-for-smart-detection',
                formulaType,
                cellAddress: currentCellAddress,
            })
        }
        return false
    }
}

// Helper to process formulas - triggers smart detection if registered, otherwise just calculates.
// Uses a two-phase approach:
// 1) Collect all formulas that should refresh.
// 2) Queue smart-detection work for those formulas and process the batch once.
const processFormulasForRefresh = async (
    context: Excel.RequestContext,
    sheet: Excel.Worksheet,
    foundRanges: Excel.RangeAreas,
    worksheetId: string,
    processedAddresses?: Set<string>
) => {
    try {
        // Track processed addresses to avoid duplicates
        const processed = processedAddresses || new Set<string>()

        // Load all addresses and formulas for the found ranges in a single round-trip
        foundRanges.areas.load('items/address,items/formulas')
        await context.sync()

        if (foundRanges.areas.items) {
            // Collect all formulas to process first, then process them in a single batch
            const formulasToProcess: Array<{
                address: string
                formula: string
                formulaType: 'WISE' | 'WISEPRICE'
                area: Excel.Range
            }> = []

            for (const area of foundRanges.areas.items) {
                const cleanAddress = area.address.includes('!')
                    ? area.address.split('!')[1]
                    : area.address

                // Skip if already processed
                if (processed.has(cleanAddress)) {
                    continue
                }

                const formula = area.formulas[0]?.[0] as string

                // First check if this formula should be refreshed at all
                if (!formula) {
                    continue
                }

                // If the formula wraps WISE/WISEPRICE (e.g. SORT(WISEPRICE(...))),
                // we can't apply smart detection safely, but we should still
                // trigger a normal recalculation so the underlying WISE*
                // call refreshes via Excel.
                const containsWiseFunction =
                    formula.includes('WISE(') || formula.includes('WISEPRICE(')
                const isDirectWiseFormula =
                    formula.startsWith('=WISE(') ||
                    formula.startsWith('=WISEPRICE(')

                if (!containsWiseFunction) {
                    continue
                }

                if (!isDirectWiseFormula) {
                    // Wrapper formula: just recalculate once and skip smart detection.
                    processed.add(cleanAddress)
                    area.calculate()
                    await context.sync()
                    continue
                }

                const formulaType = formula.startsWith('=WISE(')
                    ? 'WISE'
                    : 'WISEPRICE'
                const shouldRefresh = await shouldRefreshFormula(
                    context,
                    sheet,
                    formula,
                    formulaType
                )

                if (!shouldRefresh) {
                    continue
                }

                // Mark as processed
                processed.add(cleanAddress)

                formulasToProcess.push({
                    address: cleanAddress,
                    formula,
                    formulaType,
                    area,
                })
            }

            // Process all collected formulas - queue them all first, then process once
            for (const {
                address,
                formula,
                formulaType,
                area,
            } of formulasToProcess) {
                const baseAddress = address.split(':')[0]
                const baseMatch = baseAddress.match(/([A-Z]+)(\d+)/)
                if (!baseMatch) {
                    area.calculate()
                    await context.sync()
                    continue
                }

                const currentCellAddress = baseAddress
                let isRegistered = isFormulaRegistered(worksheetId, address)

                // If not registered by exact address, try column-based lookup (for moved formulas)
                if (!isRegistered) {
                    const formulaColumn = extractColumnFromAddress(address)
                    const formulasInColumn = getFormulasByColumn(
                        worksheetId,
                        formulaColumn
                    )

                    // Check if any formula in this column matches (formula moved)
                    if (formulasInColumn.length > 0) {
                        // Found a formula in the same column - it likely moved
                        // Update the registry with the new location
                        const oldFormula = formulasInColumn[0]
                        updateFormulaLocation(
                            worksheetId,
                            oldFormula.formulaAddress,
                            currentCellAddress
                        )
                        isRegistered = true
                    }
                }

                if (isRegistered) {
                    // Registered (either at current address or moved) - queue for smart detection
                    await handleonChangedEvent(context, sheet, {
                        address,
                        changeType: 'RangeEdited',
                        source: 'RefreshButton',
                    })
                } else {
                    // Not registered - check if it would qualify for smart detection
                    const wouldQualify = await wouldQualifyForSmartDetection(
                        context,
                        sheet,
                        formula,
                        currentCellAddress,
                        formulaType
                    )

                    if (wouldQualify) {
                        // Would qualify - queue for smart detection
                        await handleonChangedEvent(context, sheet, {
                            address,
                            changeType: 'RangeEdited',
                            source: 'RefreshButton',
                        })
                    } else {
                        // Doesn't qualify for smart detection but should refresh - just recalculate
                        area.calculate()
                        await context.sync()
                    }
                }

                // Yield periodically when processing many formulas to avoid UI lockup
                if (formulasToProcess.length > 50) {
                    await yieldToEventLoop()
                }
            }

            // After queuing all formulas, process the batch once if there are any pending requests
            if (
                formulasToProcess.length > 0 &&
                pendingFormulaRequests.size > 0 &&
                !isProcessingBatch
            ) {
                const promise = processBatchedFormulas()
                if (promise) {
                    await promise
                } else {
                    // Fallback: wait for processing to complete
                    while (
                        isProcessingBatch ||
                        pendingFormulaRequests.size > 0
                    ) {
                        await new Promise((resolve) => setTimeout(resolve, 100))
                    }
                }
            }
        }
    } catch (error) {
        handleErrorWithNotification(
            error,
            {
                operation: 'smart-detection-refresh',
                worksheetId,
            },
            {
                showNotification: true,
            }
        )
    }
}

// Enhanced version of reevaluateLiveData with smart detection support
export const reevaluateLiveDataEnhanced = async (isAllSheets: boolean) => {
    const { setLoading } = useLoadingStore.getState()

    try {
        // Clear any cached ticker scans at the start of a refresh run so we
        // always use up-to-date worksheet values.
        refreshTickerCache.clear()

        // Initial UI feedback before hitting Excel APIs – match the standard
        // "Updating data..." message and add a small note about Excel freezing.
        setLoading({
            isLoading: true,
            message: 'Updating data... Excel may freeze momentarily.',
        })
        await yieldToEventLoop()

        if (isAllSheets) {
            // 1) Get sheet ids/names in a short Excel.run to avoid a long-lived context.
            const sheetsInfo: { id: string; name: string }[] = []
            await Excel.run(async (context) => {
                const sheets = context.workbook.worksheets
                sheets.load('items/name,items/id')
                await context.sync()
                sheets.items.forEach((sheet) =>
                    sheetsInfo.push({ id: sheet.id, name: sheet.name })
                )
            })

            const totalSheets = sheetsInfo.length || 1
            let sheetIndex = 0

            // 2) Process each sheet in its own Excel.run with UI yields in between.
            for (const sheetInfo of sheetsInfo) {
                sheetIndex++

                // Keep the same core message but include lightweight sheet progress.
                setLoading({
                    isLoading: true,
                    message: `Updating data... Excel may freeze momentarily. (sheet ${sheetIndex}/${totalSheets})`,
                })
                await yieldToEventLoop()

                await Excel.run(async (context) => {
                    const sheet = context.workbook.worksheets.getItem(
                        sheetInfo.id
                    )
                    const worksheetId = sheetInfo.id

                    // Track processed addresses to avoid duplicates across searches
                    const processedAddresses = new Set<string>()

                    // WISE
                    const foundRangesWise = sheet.findAllOrNullObject('WISE', {
                        completeMatch: false,
                        matchCase: false,
                    })
                    await processFormulasForRefresh(
                        context,
                        sheet,
                        foundRangesWise,
                        worksheetId,
                        processedAddresses
                    )

                    // WISEPRICE
                    const foundRanges = sheet.findAllOrNullObject('WISEPRICE', {
                        completeMatch: false,
                        matchCase: false,
                    })
                    await processFormulasForRefresh(
                        context,
                        sheet,
                        foundRanges,
                        worksheetId,
                        processedAddresses
                    )

                    // TTM - process the same way as WISEPRICE to support smart detection
                    const foundRangesTTM = sheet.findAllOrNullObject('TTM', {
                        completeMatch: false,
                        matchCase: false,
                    })
                    await processFormulasForRefresh(
                        context,
                        sheet,
                        foundRangesTTM,
                        worksheetId,
                        processedAddresses
                    )

                    // WISEFUNDS
                    const foundRangesFunds = sheet.findAllOrNullObject(
                        'WISEFUNDS',
                        {
                            completeMatch: false,
                            matchCase: false,
                        }
                    )
                    foundRangesFunds.calculate()

                    // WISEOPTIONS
                    const foundRangesOptions = sheet.findAllOrNullObject(
                        'WISEOPTIONS',
                        {
                            completeMatch: false,
                            matchCase: false,
                        }
                    )

                    foundRangesOptions.load('areas')
                    const areas = foundRangesOptions.areas
                    areas.load('items')
                    await context.sync()

                    if (foundRangesOptions.areas.items) {
                        const rangesToCalculate =
                            foundRangesOptions.areas.items.filter((range) => {
                                const formula = range.formulas[0][0] as string
                                return (
                                    formula.split(',').length === 3 &&
                                    formula.includes('WISEOPTIONS')
                                )
                            })

                        if (rangesToCalculate.length > 0) {
                            rangesToCalculate.forEach((range) =>
                                range.calculate()
                            )
                        }
                    }

                    await context.sync()
                })

                // Yield between sheets so the task pane can update smoothly.
                await yieldToEventLoop()
            }
        } else {
            // Single sheet version – reuse standard message
            setLoading({
                isLoading: true,
                message: 'Updating data... Excel may freeze momentarily.',
            })
            await yieldToEventLoop()

            await Excel.run(async (context) => {
                const currentSheet =
                    context.workbook.worksheets.getActiveWorksheet()
                currentSheet.load('id')
                await context.sync()
                const worksheetId = currentSheet.id

                // Track processed addresses to avoid duplicates across searches
                const processedAddresses = new Set<string>()

                // WISE
                const foundRangesWise = currentSheet.findAllOrNullObject(
                    'WISE',
                    {
                        completeMatch: false,
                        matchCase: false,
                    }
                )
                await processFormulasForRefresh(
                    context,
                    currentSheet,
                    foundRangesWise,
                    worksheetId,
                    processedAddresses
                )

                // WISEPRICE
                const foundRanges = currentSheet.findAllOrNullObject(
                    'WISEPRICE',
                    {
                        completeMatch: false,
                        matchCase: false,
                    }
                )
                await processFormulasForRefresh(
                    context,
                    currentSheet,
                    foundRanges,
                    worksheetId,
                    processedAddresses
                )

                // TTM - process the same way as WISEPRICE to support smart detection
                const foundRangesTTM = currentSheet.findAllOrNullObject('TTM', {
                    completeMatch: false,
                    matchCase: false,
                })
                await processFormulasForRefresh(
                    context,
                    currentSheet,
                    foundRangesTTM,
                    worksheetId,
                    processedAddresses
                )

                // WISEFUNDS
                const foundRangesFunds = currentSheet.findAllOrNullObject(
                    'WISEFUNDS',
                    {
                        completeMatch: false,
                        matchCase: false,
                    }
                )
                foundRangesFunds.calculate()

                // WISEOPTIONS
                const foundRangesOptions = currentSheet.findAllOrNullObject(
                    'WISEOPTIONS',
                    {
                        completeMatch: false,
                        matchCase: false,
                    }
                )

                foundRangesOptions.load('areas')
                const areas = foundRangesOptions.areas
                areas.load('items')
                await context.sync()

                if (foundRangesOptions.areas.items) {
                    const rangesToCalculate =
                        foundRangesOptions.areas.items.filter((range) => {
                            const formula = range.formulas[0][0] as string
                            return (
                                formula.split(',').length === 3 &&
                                formula.includes('WISEOPTIONS')
                            )
                        })

                    if (rangesToCalculate.length > 0) {
                        rangesToCalculate.forEach((range) => range.calculate())
                    }
                }

                await context.sync()
            })
        }

        // Short “finishing up” message before clearing the loader
        setLoading({
            isLoading: true,
            message: 'Finishing up (Excel is recalculating)...',
        })
        await yieldToEventLoop()
    } catch (error) {
        // Show user-facing error for refresh failures
        handleErrorWithNotification(
            error,
            {
                operation: 'reevaluate-live-data-enhanced',
                isAllSheets,
            },
            {
                showNotification: true,
            }
        )
        throw error
    } finally {
        setLoading({ isLoading: false, message: '' })
    }
}
